import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Is the token we hold still structurally valid and unexpired? Read locally, with
// no network call, so a 401 can be judged before acting on it. This is NOT a
// security check — the server always re-verifies. It exists only so a spurious
// 401 cannot end a session that is actually fine.
function tokenStillValid() {
  const t = localStorage.getItem('auth_token');
  if (!t) return false;
  try {
    const payload = JSON.parse(atob(t.split('.')[1]));
    // No exp means we cannot judge it — treat as valid and let the server decide.
    return !payload.exp || payload.exp * 1000 > Date.now();
  } catch {
    return false;   // unparseable is dead
  }
}

// Set auth token on all axios requests
function setAxiosToken(token) {
  if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  else delete axios.defaults.headers.common['Authorization'];
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suspended, setSuspended] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('admin_token');
    setAxiosToken(null);
    setUser(null);
    setCompany(null);
    setSuspended(false);
  }, []);

  // Check if token is still valid on load
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) { setLoading(false); return; }
    setAxiosToken(token);
    axios.get('/api/auth/me')
      .then(({ data }) => {
        setUser(data.user);
        setCompany(data.company);
        if (data.company.status === 'suspended') setSuspended(true);
      })
      .catch(err => {
        if (err.response?.data?.suspended) setSuspended(true);
        else logout();
      })
      .finally(() => setLoading(false));
  }, [logout]);

  // Intercept 401/403 globally.
  //
  // A 401 is NOT proof the session is over. It also arrives when a gateway kills a
  // slow request, when a proxy interferes, or when one call among many fails. The
  // old code logged out on every one of them, so a single failed request threw the
  // user out of the dashboard mid-task.
  //
  // Now: if the token we hold is expired or unparseable, the session really is
  // over — log out. If it still looks valid, CONFIRM with /api/auth/me before
  // ejecting. If that succeeds, the credential is fine and the 401 belonged to
  // that one request, so it surfaces as an error and the session survives.
  useEffect(() => {
    let verifying = false;   // one probe at a time, however many 401s arrive
    const id = axios.interceptors.response.use(
      r => r,
      err => {
        const status = err.response?.status;
        const url = err.config?.url || '';
        if (err.response?.data?.suspended) setSuspended(true);

        const skip = url.includes('/api/auth/login') || url.includes('/api/auth/me');
        if (status === 401 && !skip) {
          if (!tokenStillValid()) {
            logout();                       // genuinely dead — eject
          } else if (!verifying) {
            verifying = true;
            // Ask the server directly. Only a 401 HERE means the credential is
            // rejected; anything else and we keep the session.
            axios.get('/api/auth/me')
              .catch(e => { if (e.response?.status === 401) logout(); })
              .finally(() => { verifying = false; });
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [logout]);

  async function login(email, password) {
    const { data } = await axios.post('/api/auth/login', { email, password });
    localStorage.setItem('auth_token', data.token);
    setAxiosToken(data.token);
    setUser(data.user);
    setCompany(data.company);
    setSuspended(false);
    return data;
  }

  // Check if user has access to a specific tool path
  function hasToolAccess(toolPath) {
    if (!company) return false;
    if (company.plan === 'suite') return true;
    return company.tools?.includes(toolPath);
  }

  return (
    <AuthContext.Provider value={{ user, company, loading, suspended, login, logout, hasToolAccess }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// Admin auth (separate)
export function getAdminToken() { return localStorage.getItem('admin_token'); }
export function setAdminToken(token) {
  if (token) localStorage.setItem('admin_token', token);
  else localStorage.removeItem('admin_token');
}
export function setUserToken(token) {
  // Used by admin impersonation — sets the company user token
  if (token) localStorage.setItem('auth_token', token);
  else localStorage.removeItem('auth_token');
}
export function getUserToken() { return localStorage.getItem('auth_token'); }
